<template>
  <div v-if="pageLoading">
    <div class="flex items-center justify-center w-full">
      <PreloaderIcon class="mx-auto opacity-100 text-zuno-turquoise" />
    </div>
  </div>
  <div
    v-else
    id="historical-table"
    class="w-full"
  >
    <div class="space-y-4">
      <div class="bg-white sm:rounded-lg w-full  overflow-scroll">
        <div class="flex items-center gap-2 px-6 py-4">
          <span class="text-lg text-primary">Data</span>
          <div
            class="default-badge font-inter w-fit select-none rounded-full bg-main-primary/10 px-3 py-1 text-center text-main-primary"
          >
            <span class="block text-xs">{{ historicalTableData.length }} data</span>
          </div>
        </div>
        <table class="divide-y divide-slate-100">
          <thead class="whitespace-nowrap tracking-wide">
            <tr class="text-center text-xs font-bold text-gray-700">
              <th class="py-2 uppercase">
                Year
              </th>
              <th class="py-2">
                <span class="uppercase">Total Emissions </span> <span class="no-localize-translate">(tCO2e)</span>
              </th>
              <th class="py-2">
                <span class="uppercase">Scope 1 </span><span class="no-localize-translate">(tCO2e)</span>
              </th>
              <th class="py-2">
                <span class="uppercase">Scope 2 </span><span class="no-localize-translate">(tCO2e)</span>
              </th>
              <th class="py-2">
                <span class="uppercase">Scope 3 </span><span class="no-localize-translate">(tCO2e)</span>
              </th>
              <th class="py-2">
                <span class="uppercase">Data Source</span>
              </th>
              <th class="py-2 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(data, rowIndex) in historicalTableData"
              :key="`historical-${rowIndex}`"
              class="whitespace-nowrap text-sm"
              :class="{
                'border border-[#C14B3A] sm:rounded-lg': data.rowHasErrors,
              }"
            >
              <td class="px-2 py-1">
                <DropDownInput
                  :options="years"
                  :first-value="historicalTableData[rowIndex].year"
                  classes="w-[110px]"
                  @value-updated="
                    historicalTableData[rowIndex].year = $event;
                    handleYearChange(rowIndex);
                  "
                />
              </td>
              <td class="py-1 text-center">
                {{ historicalTableData[rowIndex].total_emissions }}
              </td>
              <td class="px-2 py-1">
                <TextInput
                  :val="historicalTableData[rowIndex].scope_one"
                  classes="w-[120px]"
                  placeholder="Optional..."
                  @input="historicalTableData[rowIndex].scope_one = $event"
                  @focusout="formatScope($event, rowIndex, 1)"
                />
              </td>
              <td class="px-2 py-1">
                <TextInput
                  :val="historicalTableData[rowIndex].scope_two"
                  classes="w-[120px]"
                  placeholder="Optional..."
                  @input="historicalTableData[rowIndex].scope_two = $event"
                  @focusout="formatScope($event, rowIndex, 2)"
                />
              </td>
              <td class="px-2 py-1">
                <TextInput
                  :val="historicalTableData[rowIndex].scope_three"
                  classes="w-[120px]"
                  placeholder="Optional..."
                  @input="historicalTableData[rowIndex].scope_three = $event"
                  @focusout="formatScope($event, rowIndex, 3)"
                />
              </td>
              <td class="py-1">
                <div v-if="historicalTableData[rowIndex].showSourceToggle">
                  <label
                    class="relative inline-flex cursor-pointer items-center"
                  >
                    <input
                      v-model="historicalTableData[rowIndex].active_toggle"
                      type="checkbox"
                      :checked="historicalTableData[rowIndex].active_toggle"
                      class="peer sr-only"
                      @change="toggleChanged(rowIndex, $event)"
                    >
                    <div
                      class="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-main-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-main-primary/50"
                    />
                    <span class="ml-3 font-medium text-gray-900">Historical Data</span>
                  </label>
                </div>
              </td>
              <div class="px-6 py-4">
                <DestructivePrimaryButton
                  :disabled="
                    historicalTableData.length === 1
                      && !historicalTableData[rowIndex].id
                  "
                  @click="deleteHistoricalRow(rowIndex)"
                >
                  Delete
                </DestructivePrimaryButton>
              </div>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between py-1.5 text-sm">
        <PrimaryButton @click="addHistoricalRow">
          <PlusSmall custom-style="h-5 w-5" />
        </PrimaryButton>
        <div class="flex items-center gap-2">
          <GhostButton @click="$emit('goBack')">
            Cancel
          </GhostButton>
          <PrimaryButton
            :disabled="cantSaveHistoricalData"
            @click="saveHistoricalData"
          >
            Save
          </PrimaryButton>
        </div>
      </div>
      <p class="text-sm italic text-gray-500">
        If you would like to input historical emissions data at source level,
        please
        <NuxtLink
          to="/dashboard/emission-sources"
          class="underline"
        >click here!
        </NuxtLink>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GhgHistoricalDataTable',

  emits: ['goBack'],

  data() {
    const currentYear = new Date().getFullYear()
    const years = Array.from(Array(currentYear).keys())
      .filter(year => year > 1899)
      .reverse()

    return {
      years,
      veridisYears: [],
      historicalTableData: [this.createEmptyRow()],
      updatedIds: [],
      companyId: '',
      pageLoading: true,
    }
  },

  computed: {
    historicalDataHasErrors() {
      return this.historicalTableData.some(this.rowHasErrors)
    },
    cantSaveHistoricalData() {
      return (
        this.historicalDataHasErrors
        || this.historicalTableData.some(
          ({ scope_one, scope_two, scope_three }) =>
            ![scope_one, scope_two, scope_three].some(val => val),
        )
      )
    },
  },

  watch: {
    historicalTableData: {
      deep: true,
      handler(newData, oldData) {
        if (oldData.length === 1 && oldData[0].total_emissions === '') return
        newData.forEach((item) => {
          if (item.id) {
            const oldHistory = oldData
              .filter(old => old.id)
              .find(({ id }) => id === item.id)
            if (
              item.year !== oldHistory.year
              || item.scope_one !== oldHistory.scope_one
              || item.scope_two !== oldHistory.scope_two
              || item.scope_three !== oldHistory.scope_three
            ) {
              this.updatedIds.push(item.id)
              this.updatedIds = [...new Set(this.updatedIds)]
            }
          }
        })
      },
    },
  },

  mounted() {
    this.companyId = localStorage.getItem('cid')
    const { companyEmissions, emissionTracker } = this.$apis

    Promise.all([
      companyEmissions.listActivityDataYearly(),
      emissionTracker.companyHistoricalData(this.companyId),
    ]).then((responses) => {
      const [{ data: veridisYearsData }, { data: savedHistoricalData }]
        = responses
      this.veridisYears = veridisYearsData

      if (savedHistoricalData.length) {
        this.historicalTableData = savedHistoricalData.map((item) => {
          item.showSourceToggle = this.veridisYears.includes(item.year)
          item.rowHasErrors = false
          item.total_emissions = thousands(item.total_emissions, 4)
          item.scope_one = this.formatScopeData(item.scope_one)
          item.scope_two = this.formatScopeData(item.scope_two)
          item.scope_three = this.formatScopeData(item.scope_three)
          return item
        })
      }

      this.pageLoading = false
    })
  },

  methods: {
    createEmptyRow() {
      return {
        id: null,
        company: this.companyId,
        showSourceToggle: false,
        rowHasErrors: false,
        year: '',
        total_emissions: '',
        scope_one: '',
        scope_two: '',
        scope_three: '',
        active_toggle: false,
      }
    },
    formatScopeData(value) {
      if (value) {
        return thousands(value, 4) === '0.0000' ? '' : thousands(value, 4)
      }
      return ''
    },
    formatScope(value, index, scope) {
      if (value === '') return
      let scopeKey = 'scope_one'
      if (parseInt(scope) === 2) {
        scopeKey = 'scope_two'
      }
      if (parseInt(scope) === 3) {
        scopeKey = 'scope_three'
      }

      this.historicalTableData[index][scopeKey] = thousands(
        undoThousands(value),
        4,
      )

      nextTick(() => {
        const { scope_one, scope_two, scope_three }
          = this.historicalTableData[index]
        const scopeOne = scope_one ? undoThousands(scope_one) : 0
        const scopeTwo = scope_two ? undoThousands(scope_two) : 0
        const scopeThree = scope_three ? undoThousands(scope_three) : 0
        this.historicalTableData[index].total_emissions = thousands(
          scopeOne + scopeTwo + scopeThree,
          4,
        )
      })
    },
    toggleChanged(index, event) {
      const { id, year } = this.historicalTableData[index]

      this.$apis.emissionTracker.updateCompanyHistoricalData(id, {
        company: localStorage.getItem('cid'),
        year,
        active_toggle: event.target.checked,
      })
    },
    deleteHistoricalRow(index) {
      if (this.historicalTableData[index].id) {
        this.$apis.emissionTracker
          .deleteCompanyHistoricalData(this.historicalTableData[index].id)
          .then(() => this.historicalTableData.splice(index, 1))
      }
      else {
        this.historicalTableData.splice(index, 1)
      }
    },
    addHistoricalRow() {
      this.historicalTableData.push(this.createEmptyRow())
    },
    handleYearChange(rowIndex) {
      this.historicalTableData[rowIndex].showSourceToggle
        = this.veridisYears.includes(
          parseInt(this.historicalTableData[rowIndex].year),
        )
    },
    saveHistoricalData() {
      const postData = []
      this.historicalTableData.forEach(item =>
        postData.push({
          company: localStorage.getItem('cid'),
          ...item,
        }),
      )
      const transformedData = [...postData].map((history) => {
        if (!history.scope_one) {
          delete history.scope_one
        }
        else {
          history.scope_one = undoThousands(history.scope_one)
        }
        if (!history.scope_two) {
          delete history.scope_two
        }
        else {
          history.scope_two = undoThousands(history.scope_two)
        }
        if (!history.scope_three) {
          delete history.scope_three
        }
        else {
          history.scope_three = undoThousands(history.scope_three)
        }
        delete history.rowHasErrors
        delete history.showSourceToggle
        delete history.total_emissions
        return history
      })

      if ([...transformedData].filter(item => !item.id).length) {
        this.saveNewData([...transformedData].filter(item => !item.id))
      }
      const updateData = [...transformedData].filter(
        item => item.id && this.updatedIds.includes(item.id),
      )

      if (updateData.length) {
        Promise.all(
          updateData.map(updateDatum =>
            this.$apis.emissionTracker.updateCompanyHistoricalData(
              updateDatum.id,
              updateDatum,
            ),
          ),
        ).then(() => {
          this.$toast.success('Data updated successfully!', {
            duration: 3000,
            position: 'top-right',
          })
        })
      }
    },
    saveNewData(createData) {
      this.$apis.emissionTracker
        .multiCreateHistoricalData(createData)
        .then(() => {
          this.$toast.success('Data updated successfully!', {
            duration: 3000,
            position: 'top-right',
          })
          this.$emit('goBack')
        })
    },
    rowHasErrors(row, index, array) {
      if (this.isEmptyRow(row)) {
        return false
      }
      if (this.hasInvalidYear(row, array)) {
        return true
      }

      if (this.hasNegativeScopes(row, index)) {
        return true
      }

      return false
    },

    isEmptyRow(row) {
      if (
        row.id === null
        && row.year === ''
        && row.total_emissions === ''
        && row.scope_one === ''
        && row.scope_two === ''
        && row.scope_three === ''
      ) {
        return true
      }
      return false
    },

    hasInvalidYear(row, array) {
      if (row.id === null) return false
      if (row.year === '') {
        if (array.length === 1) {
          return true
        }
        this.$toast.error('Please input a year value', {
          duration: 5000,
          position: 'top-right',
        })
        return true
      }
      return false
    },

    hasNegativeScopes(row, index) {
      const hasNegative = [row.scope_one, row.scope_two, row.scope_three].some(
        val => undoThousands(val) < 0,
      )

      if (hasNegative) {
        this.historicalTableData[index].rowHasErrors = true
        this.$toast.error('Please input a positive value', {
          duration: 5000,
          position: 'top-right',
        })
        return true
      }

      this.historicalTableData[index].rowHasErrors = false
      return false
    },
  },
}
</script>
